import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { Activity, Loader2, ChevronRight, RefreshCw } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";

const BAND_META = {
  excellent: { label: "Excellent", color: "#34D399", bg: "rgba(52,211,153,0.10)" },
  good: { label: "Good", color: "#A3E635", bg: "rgba(163,230,53,0.10)" },
  watch: { label: "Watch", color: "#FBBF24", bg: "rgba(251,191,36,0.10)" },
  poor: { label: "Needs work", color: "#F87171", bg: "rgba(248,113,113,0.10)" },
};

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

/**
 * Quarterly Tax Health Check — one-tap deterministic scorecard for the currently
 * selected BAS quarter. Powered by /api/tax/health-check.
 */
export default function QuarterlyHealthCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: res } = await api.get("/tax/health-check");
      setData(res);
    } catch (err) {
      devLog.warn("health check failed", err);
      toast.error("Health check failed");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const band = data?.band ? BAND_META[data.band] : null;
  const overall = data?.overall ?? 0;
  const dims = data?.dimensions || [];
  const actions = [...(data?.actions || [])].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)
  );

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="quarterly-health">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 text-[#FF9500]">
          <Activity className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-heading">
            Quarterly Tax Health
          </span>
        </div>
        <button
          type="button"
          data-testid="qh-refresh"
          onClick={load}
          disabled={busy}
          className="ml-auto h-7 w-7 rounded-lg text-white/50 hover:text-white flex items-center justify-center disabled:opacity-40"
          title="Re-run health check"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center relative"
          style={{ background: `conic-gradient(${band?.color || "#6B7280"} ${overall}%, rgba(255,255,255,0.06) 0)` }}
          data-testid="qh-score-ring"
        >
          <div className="absolute inset-2 rounded-full bg-[#1F1F1F] flex flex-col items-center justify-center">
            <div className="font-heading font-extrabold text-2xl tabular-nums leading-none">
              {overall}
            </div>
            <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">score</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.2em] font-heading"
            style={{ color: band?.color }}
          >
            {band?.label || "—"}
          </div>
          <div className="font-heading font-extrabold text-lg leading-tight truncate">
            {data?.quarter?.label || "This quarter"}
          </div>
          {data?.entry_counts && (
            <div className="text-[11px] text-white/50 mt-0.5">
              {data.entry_counts.total} entries · {data.entry_counts.approved} approved ·{" "}
              {data.entry_counts.pending} pending
            </div>
          )}
        </div>
      </div>

      {dims.length > 0 && (
        <div className="space-y-1.5" data-testid="qh-dims">
          {dims.map((d) => {
            const c = d.score >= 85 ? "#34D399" : d.score >= 65 ? "#A3E635" : d.score >= 40 ? "#FBBF24" : "#F87171";
            return (
              <div key={d.key} className="flex items-center gap-2 text-xs">
                <div className="text-white/70 w-40 shrink-0 truncate">{d.label}</div>
                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full transition-[width]"
                    style={{ width: `${d.score}%`, background: c }}
                  />
                </div>
                <div
                  className="w-8 text-right tabular-nums font-semibold text-[11px]"
                  style={{ color: c }}
                >
                  {d.score}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {actions.length > 0 && (
        <div className="space-y-1.5" data-testid="qh-actions">
          <div className="text-[10px] uppercase tracking-[0.2em] font-heading text-white/50 pt-1">
            Next best moves
          </div>
          {actions.slice(0, 4).map((a, i) => (
            <button
              key={`${a.route}-${i}`}
              type="button"
              data-testid={`qh-action-${i}`}
              onClick={() => a.route && nav(a.route)}
              disabled={!a.route}
              className="w-full text-left rounded-lg p-2.5 bi-surface-2 flex items-center gap-2 hover:brightness-110 transition disabled:opacity-60 disabled:cursor-default"
              style={{
                background: band?.bg || "rgba(255,255,255,0.03)",
              }}
            >
              <span
                className="text-[9px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                style={{
                  background: a.priority === "high" ? "#F8717122" : a.priority === "medium" ? "#FBBF2422" : "#6B728022",
                  color: a.priority === "high" ? "#F87171" : a.priority === "medium" ? "#FBBF24" : "#9CA3AF",
                }}
              >
                {a.priority}
              </span>
              <span className="flex-1 min-w-0 text-xs text-white/85 leading-snug">{a.text}</span>
              <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />
            </button>
          ))}
        </div>
      )}

      <TaxDisclaimer compact />
    </section>
  );
}
